import { FunctionComponent, lazy, Suspense } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';

// components
import { Hold } from './Hold';
import { PrivateOfferItemPrices } from './PrivateOfferItemPrices';
import { SaleItemPrices } from './SaleItemPrices';
import { NetItemPrices } from './NetItemPrices';
import { RetailItemPrices } from './RetailItemPrices';
import { PriceAmount } from './PriceAmount';
import { textPriceValue } from './messages/priceMessages';

import { PRICE_TYPES, TEXT_SIZE_SMALL, TEXT_SIZE_MEDIUM } from './constants';

const UnavailableIcon = lazy(
    () =>
        import(
            /* webpackChunkName: "PriceDisplayUnavailableIcon" */ 'dibs-icons/exports/Unavailable'
        )
);

const SoldSvgIcon = lazy(
    () => import(/* webpackChunkName: "PriceDisplaySoldIcon" */ 'dibs-icons/exports/Sold')
);

// helpers
import { getPriceFromAmountListByCurrency, isValidTextOnlyPriceType } from './helpers';

// styles
import styles from './PriceDisplay.scss';

// types
import { PriceDisplay_item as ItemType } from './__generated__/PriceDisplay_item.graphql';
import { PriceDisplay_sku as SkuType } from './__generated__/PriceDisplay_sku.graphql';

type Props = {
    currency?: string;
    item: ItemType;
    sku?: SkuType | null;
    textSize?: string;
    useSvgIcon?: boolean;
};

const getPriceLabel = (
    isClient: boolean,
    useSvgIcon: boolean,
    textType: string
): JSX.Element | null => {
    if (useSvgIcon && [PRICE_TYPES.SOLD, PRICE_TYPES.UNAVAILABLE].includes(textType)) {
        if (!isClient) {
            // We are in ReactDOMServer, we can't show lazy-loaded <svg> icon as lazy-loading not implemented to server yet:
            // https://reactjs.org/docs/code-splitting.html#:~:text=are%20not%20yet%20available%20for%20server-side%20rendering
            //
            // Fallback for SOLD and UNAVAILABLE is to return null as we want to hide these keywords from indexing bots.
            return null;
        } else if (textType === PRICE_TYPES.SOLD) {
            return (
                <Suspense fallback="SOLD">
                    <SoldSvgIcon className={styles.icon} />
                </Suspense>
            );
        } else if (textType === PRICE_TYPES.UNAVAILABLE) {
            return (
                <Suspense fallback="UNAVAILABLE">
                    <UnavailableIcon className={styles.icon} />
                </Suspense>
            );
        }
    }

    // most basic case - return label as text
    return textPriceValue(textType);
};

export const PriceDisplayComponent: FunctionComponent<Props> = ({
    item,
    sku,
    currency = 'USD',
    textSize = TEXT_SIZE_MEDIUM,
    useSvgIcon = false,
}) => {
    const isClient = useClientState();

    const holdDisplayTime = item?.activeHold?.displayTimeToExpire || null;
    const itemPriceDisplay = sku ? sku?.displayPrice?.[0] : item?.displayPrice?.[0];

    if (!itemPriceDisplay) {
        return <div className={styles.wrapper} />;
    }

    const {
        convertedAmountList,
        originalConvertedAmountList,
        privateOfferConvertedAmountList,
        amountType,
        textType,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        offerExpirationDate,
    } = itemPriceDisplay;

    const showHold = textType === PRICE_TYPES.HOLD || textType === PRICE_TYPES.HOLD_CUSTOM;

    const price = getPriceFromAmountListByCurrency(convertedAmountList, currency);
    const originalPrice = getPriceFromAmountListByCurrency(originalConvertedAmountList, currency);
    const percentageValue = percentageOff ? parseInt(percentageOff) : undefined;
    const isPriceValid = typeof price === 'number' && price > 0;

    // Getting priceComponent
    let priceComponent = null;
    const sharedPriceProps = {
        price,
        originalPrice,
        currency,
        quantityDisplay,
        hasPrivateOffer: !!privateOfferConvertedAmountList,
    };

    if (isValidTextOnlyPriceType(textType)) {
        const showPrice = price && textType !== PRICE_TYPES.PUR;
        const priceLabel = getPriceLabel(isClient, useSvgIcon, textType || '');

        // Item is unavailable or has no visible price (e.g. sold, unavailable or pur)
        priceComponent = (
            <div className={styles.soldUnavailablePriceWrapper}>
                <div
                    className={classnames({
                        [styles.sold]: textType === PRICE_TYPES.SOLD,
                        [styles.unavailable]: textType === PRICE_TYPES.UNAVAILABLE,
                        [styles.pur]: textType === PRICE_TYPES.PUR,
                    })}
                    data-tn={`price-${textType}-price`}
                >
                    {priceLabel}
                </div>
                {showPrice && (
                    <PriceAmount
                        type={
                            amountType === PRICE_TYPES.FINAL
                                ? PRICE_TYPES.FINAL
                                : PRICE_TYPES.RETAIL
                        }
                        currency={currency}
                        amount={price}
                        showLabel
                        quantityDisplay={quantityDisplay}
                        isUnpurchasable={textType !== PRICE_TYPES.PUR}
                    />
                )}
            </div>
        );
    } else if (isPriceValid && !isValidTextOnlyPriceType(textType)) {
        if (amountType === PRICE_TYPES.SALE) {
            // Item is on sale
            priceComponent = (
                <SaleItemPrices {...sharedPriceProps} percentageOff={percentageValue} />
            );
        } else if (amountType === PRICE_TYPES.NET) {
            // Item has a net price and is being viewed by a trade user
            priceComponent = (
                <NetItemPrices {...sharedPriceProps} percentageOff={percentageValue} />
            );
        } else if (amountType === PRICE_TYPES.RETAIL) {
            // Item only has a retail price (or is being viewed by a non-trade user)
            priceComponent = (
                <RetailItemPrices {...sharedPriceProps} showPriceLabel={!!showPriceLabel} />
            );
        }
    }

    // Items can have holds, but not valid price e.g. PUR items
    if (!priceComponent && !showHold) {
        return null;
    }

    let privateOffer;
    if (privateOfferConvertedAmountList) {
        const privateOfferPrice = getPriceFromAmountListByCurrency(
            privateOfferConvertedAmountList,
            currency
        );

        privateOffer = (
            <PrivateOfferItemPrices
                {...sharedPriceProps}
                price={privateOfferPrice}
                expiration={offerExpirationDate}
                showExpirationDate={!holdDisplayTime}
            />
        );
    }

    const priceWrapperClasses = classnames(styles.wrapper, {
        [styles.smallText]: textSize === TEXT_SIZE_SMALL,
    });

    return (
        <div className={priceWrapperClasses} data-tn="price-wrapper">
            {/* 'On Hold For You' OR 'On Hold' */}
            {(textType === PRICE_TYPES.HOLD || textType === PRICE_TYPES.HOLD_CUSTOM) && (
                <Hold holdType={textType} holdDisplayTime={holdDisplayTime} />
            )}
            {/* show Private Offer if exists */}
            {privateOffer}
            {/* Price */}
            {priceComponent}
        </div>
    );
};

export const PriceDisplay = createFragmentContainer(PriceDisplayComponent, {
    item: graphql`
        fragment PriceDisplay_item on Item
        @argumentDefinitions(
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
        ) {
            activeHold {
                displayTimeToExpire
            }
            displayPrice(isTrade: $isTrade, buyerId: $userId) {
                originalConvertedAmountList {
                    currency
                    amount
                }
                convertedAmountList {
                    currency
                    amount
                }
                privateOfferConvertedAmountList {
                    currency
                    amount
                }
                quantityDisplay
                percentageOff
                textType
                amountType
                showPriceLabel
                showPriceVariability
                offerExpirationDate
            }
        }
    `,
    sku: graphql`
        fragment PriceDisplay_sku on ItemSkuType
        @argumentDefinitions(
            hasSkuId: { type: "Boolean", defaultValue: false }
            userId: { type: "String", defaultValue: "" }
            isTrade: { type: "Boolean", defaultValue: false }
        ) {
            ... @include(if: $hasSkuId) {
                serviceId
                displayPrice(isTrade: $isTrade, buyerId: $userId) {
                    originalConvertedAmountList {
                        currency
                        amount
                    }
                    convertedAmountList {
                        currency
                        amount
                    }
                    privateOfferConvertedAmountList {
                        currency
                        amount
                    }
                    quantityDisplay
                    percentageOff
                    textType
                    amountType
                    showPriceLabel
                    showPriceVariability
                    offerExpirationDate
                }
            }
        }
    `,
});
